import React, { useState, useEffect } from 'react';
import Button from '../ui/Button';
import QRCodeGenerator from '../ui/QRCodeGeneratorNew';
import { QrCode, Copy, Timer, CheckCircle, Clock, Wallet, X, TrendingUp } from 'lucide-react';

const RechargeModal = ({ 
  isOpen, 
  onClose, 
  selectedStudent, 
  onCreatePixPayment,
  onCancelPixPayment,
  onCopyPixCode,
  formatCurrency,
  pixData,
  paymentStatus,
  pixLoading,
  paymentTimer,
  pendingPix,
  onRestorePendingPix
}) => {
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [localLoading, setLocalLoading] = useState(false);
  const [hasRestoredPix, setHasRestoredPix] = useState(false);

  // Limpar campos quando modal fecha
  useEffect(() => {
    if (!isOpen) {
      setRechargeAmount('');
      setHasRestoredPix(false);
    }
  }, [isOpen]);

  // Restaurar valores do PIX pendente quando o modal abrir (apenas uma vez)
  useEffect(() => {
    if (isOpen && 
        pendingPix && 
        pendingPix.studentId === selectedStudent?.id && 
        !pixData && 
        !hasRestoredPix) {
      
      console.log('🔄 Restaurando PIX pendente:', pendingPix);
      setRechargeAmount(pendingPix.amount?.toString() || '');
      setHasRestoredPix(true);
      
      // Notificar o componente pai para restaurar o estado do PIX
      if (onRestorePendingPix) {
        onRestorePendingPix(pendingPix);
      }
    }
  }, [isOpen, pendingPix, selectedStudent?.id, pixData, hasRestoredPix, onRestorePendingPix]);  const handleClose = async () => {
    // Se há PIX pendente, cancelar antes de fechar
    if (pixData && paymentStatus === 'pending' && onCancelPixPayment) {
      try {
        await onCancelPixPayment();
      } catch (error) {
        console.error('Erro ao cancelar PIX:', error);
      }
    }
    
    setRechargeAmount('');
    onClose();
  };

  const handleCreatePixPayment = async () => {
    if (!rechargeAmount || parseFloat(rechargeAmount) < 5) return;
    
    setLocalLoading(true);
    try {
      await onCreatePixPayment(rechargeAmount);
    } finally {
      setLocalLoading(false);
    }
  };

  const isFormValid = rechargeAmount && parseFloat(rechargeAmount) >= 5;
  const isButtonDisabled = pixLoading || localLoading || !isFormValid || (pixData && paymentStatus !== 'expired');

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200 slide-in-from-bottom-4">        {/* Header Moderno */}
        <div className="relative overflow-hidden">
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-5 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Recarregar Saldo</h3>
                  <p className="text-green-100 text-sm font-medium">{selectedStudent?.name}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                disabled={pixLoading || localLoading}
                className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-colors"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          {/* Decorative wave */}
          <div className="absolute bottom-0 left-0 right-0 h-3 bg-white">
            <svg viewBox="0 0 100 20" className="w-full h-full fill-green-500">
              <path d="M0,20 Q25,0 50,10 T100,5 L100,20 Z" />
            </svg>
          </div>
        </div>        {/* Content - Layout em Grid Responsivo */}
        <div className="px-6 py-4">
          {/* Notificação de PIX Pendente */}
          {pendingPix && pendingPix.studentId === selectedStudent?.id && !pixData && (
            <div className="mb-4 bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-400 rounded-lg p-4 animate-in slide-in-from-top duration-300">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <Clock className="w-5 h-5 text-amber-600 mt-0.5" />
                </div>
                <div className="ml-3 flex-1">
                  <h4 className="text-sm font-semibold text-amber-800">
                    PIX Pendente Encontrado
                  </h4>
                  <p className="text-sm text-amber-700 mt-1">
                    Você tem um PIX de {formatCurrency(pendingPix.amount)} pendente para este aluno. 
                    Escaneie o QR Code abaixo para concluir o pagamento.
                  </p>
                  <div className="mt-2 text-xs text-amber-600">
                    Gerado em: {new Date(pendingPix.createdAt).toLocaleString('pt-BR')}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Coluna Esquerda - Formulário */}
            <div className="space-y-4">
              {/* Amount Input Moderno */}
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-gray-800">
                  Valor da Recarga
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <span className="text-gray-500 font-medium">R$</span>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="5.00"
                    value={rechargeAmount}
                    onChange={(e) => setRechargeAmount(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 text-lg font-bold border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-4 focus:ring-green-500/20 transition-all duration-200 bg-gray-50 focus:bg-white disabled:opacity-50"
                    placeholder="0,00"
                    disabled={pixLoading || localLoading || paymentStatus === 'pending'}
                  />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Valor mínimo: R$ 5,00</span>
                  {rechargeAmount && parseFloat(rechargeAmount) >= 5 && (
                    <span className="text-green-600 font-medium animate-in slide-in-from-right duration-200">
                      ✓ Valor válido
                    </span>
                  )}
                </div>
              </div>              {/* Balance Preview Moderno - Sempre Visível */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 animate-in slide-in-from-top duration-300">
                <h4 className="text-sm font-semibold text-blue-800 mb-3 flex items-center">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Resumo da Recarga
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-blue-700">Saldo atual:</span>
                    <span className="font-bold text-blue-900">{formatCurrency(selectedStudent?.balance)}</span>
                  </div>
                  <div className="h-px bg-blue-200"></div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-blue-700">Após recarga:</span>
                    <span className="font-bold text-lg text-green-600">
                      {rechargeAmount && parseFloat(rechargeAmount) > 0 
                        ? formatCurrency((selectedStudent?.balance || 0) + parseFloat(rechargeAmount))
                        : formatCurrency(selectedStudent?.balance || 0)
                      }
                    </span>
                  </div>
                  {rechargeAmount && parseFloat(rechargeAmount) > 0 && (
                    <div className="mt-2 pt-2 border-t border-blue-200">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-blue-600">Valor da recarga:</span>
                        <span className="font-bold text-sm text-blue-800">
                          + {formatCurrency(parseFloat(rechargeAmount))}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Info de Segurança Moderna */}
              {!pixData && (
                <div className="bg-gradient-to-r from-gray-50 to-blue-50 border border-gray-200 rounded-xl p-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <QrCode className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1 text-sm">PIX Instantâneo e Seguro</h4>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        Pagamento processado pelo <span className="font-medium text-blue-600">Asaas</span> com tecnologia PIX do Banco Central. 
                        Crédito automático em sua conta.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Coluna Direita - PIX (se ativo) */}
            {pixData && (
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-4 animate-in zoom-in-95 duration-300">
                <div className="text-center space-y-3">
                  {/* Header do PIX */}
                  <div className="inline-flex items-center space-x-2 bg-green-100 px-3 py-1.5 rounded-full">
                    <QrCode className="w-4 h-4 text-green-600" />
                    <h4 className="font-bold text-green-800 text-sm">PIX Gerado com Sucesso</h4>
                  </div>

                  {/* QR Code com estilo moderno */}
                  <div className="relative">
                    <div className="bg-white p-3 rounded-2xl shadow-lg mx-auto w-fit">
                      <QRCodeGenerator 
                        value={pixData.pixQrCode?.payload}
                        size={160}
                        className="rounded-lg"
                      />
                    </div>
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                  </div>

                  {/* PIX Code com design moderno */}
                  {pixData.pixQrCode?.payload && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-green-800">
                        Ou copie o código PIX:
                      </p>
                      <div className="bg-white/80 backdrop-blur-sm p-2 rounded-xl border border-green-200 text-xs break-all font-mono max-h-14 overflow-y-auto">
                        {pixData.pixQrCode.payload}
                      </div>
                      <Button
                        variant="outline"
                        onClick={onCopyPixCode}
                        className="bg-white text-green-700 border-green-300 hover:bg-green-50 hover:border-green-400 transition-all duration-200 font-medium w-full"
                        size="sm"
                      >
                        <Copy className="w-3 h-3 mr-2" />
                        Copiar Código PIX
                      </Button>
                    </div>
                  )}

                  {/* Payment Status */}
                  <PaymentStatus
                    status={paymentStatus}
                    onCancelPayment={onCancelPixPayment}
                    pixLoading={pixLoading}
                  />
                </div>
              </div>
            )}
          </div>
        </div>        {/* Footer Moderno */}
        <div className="px-6 py-4 bg-gray-50 rounded-b-2xl border-t border-gray-200">
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={pixLoading || localLoading}
              className={`flex-1 py-2.5 font-medium transition-all duration-200 ${
                pixData && paymentStatus === 'pending' 
                  ? 'border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400' 
                  : 'border-gray-300 hover:bg-gray-100'
              }`}
              size="sm"
            >
              {pixLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-red-300 border-t-red-600 rounded-full animate-spin mr-2" />
                  Cancelando PIX...
                </div>
              ) : pixData && paymentStatus === 'pending' ? (
                <div className="flex items-center justify-center">
                  <X className="w-4 h-4 mr-2" />
                  Cancelar PIX
                </div>
              ) : (
                'Cancelar'
              )}
            </Button>
            
            <Button
              onClick={handleCreatePixPayment}
              disabled={isButtonDisabled}
              className="flex-1 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              size="sm"
            >
              {(pixLoading || localLoading) ? (
                <div className="flex items-center">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  Gerando PIX...
                </div>
              ) : pixData && paymentStatus === 'paid' ? (
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Pagamento Confirmado
                </div>
              ) : pixData && paymentStatus === 'pending' ? (
                <div className="flex items-center">
                  <Timer className="w-4 h-4 mr-2" />
                  Aguardando Pagamento
                </div>
              ) : pixData && paymentStatus === 'expired' ? (
                <div className="flex items-center">
                  <QrCode className="w-4 h-4 mr-2" />
                  Gerar Novo PIX
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <QrCode className="w-4 h-4 mr-2" />
                  Gerar PIX
                </div>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PaymentStatus = ({ status, onCancelPayment, pixLoading }) => {
  if (status === 'pending') {
    return (
      <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-3">
        <div className="flex items-center justify-center text-yellow-700">
          <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center mr-2">
            <Timer className="w-4 h-4 animate-pulse" />
          </div>
          <div className="text-center">
            <p className="font-bold text-sm">Aguardando Pagamento</p>
            <p className="text-xs text-yellow-600">Escaneie o QR Code ou cole o código PIX</p>
            <p className="text-xs text-yellow-500 mt-1">Use o botão "Cancelar" para cancelar o PIX</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (status === 'paid') {
    return (
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-3 animate-in zoom-in-95 duration-300">
        <div className="flex items-center justify-center text-green-700">
          <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-2">
            <CheckCircle className="w-4 h-4" />
          </div>
          <div className="text-center">
            <p className="font-bold text-sm">Pagamento Confirmado!</p>
            <p className="text-xs text-green-600">O saldo foi creditado automaticamente</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (status === 'expired') {
    return (
      <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl p-3">
        <div className="flex items-center justify-center text-red-700">
          <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mr-2">
            <Clock className="w-4 h-4" />
          </div>
          <div className="text-center">
            <p className="font-bold text-sm">PIX Expirado</p>
            <p className="text-xs text-red-600">Gere um novo PIX para continuar</p>
          </div>
        </div>
      </div>
    );
  }
  
  return null;
};

export default RechargeModal;